'use client';

import { ChevronLeft, ChevronRight } from '@shared/icons';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import * as React from 'react';
import { cn } from '../lib/utils';
import { Button } from './Button/Button';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  fullWidth?: boolean;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  fullWidth: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

function Carousel({ orientation = 'horizontal', opts, setApi, plugins, className, children, fullWidth = false, ...props }: React.ComponentProps<'div'> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        fullWidth,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn('relative', className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
        data-track-container="Carousel"
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<'div'>) {
  const { carouselRef, orientation, fullWidth } = useCarousel();

  const touchAction = orientation === 'horizontal' ? 'pan-y' : 'pan-x';

  return (
    <div
      ref={carouselRef}
      className={cn('overflow-hidden', fullWidth ? '' : 'pr-[var(--ll-container-padding)] lg:pr-[var(--ll-lg-right-carousel-padding,0)]')}
      data-slot="carousel-content"
      style={{ touchAction }}
    >
      <div className={cn('flex', orientation === 'horizontal' ? '-ml-8' : '-mt-8 flex-col', className)} {...props} />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation } = useCarousel();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn('min-w-0 shrink-0 grow-0', orientation === 'horizontal' ? 'pl-8' : 'pt-8', className)}
      {...props}
    />
  );
}

function CarouselPrevious({ className, variant = 'secondary', size = 'l', ...props }: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev, canScrollNext } = useCarousel();

  if (!canScrollPrev && !canScrollNext) return null;

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn('absolute', orientation === 'horizontal' ? 'top-1/2 -left-12 -translate-y-1/2' : '-top-24 left-1/2 -translate-x-1/2 rotate-90', className)}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ChevronLeft size="small" />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({ className, variant = 'secondary', size = 'l', ...props }: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext, canScrollPrev } = useCarousel();

  if (!canScrollPrev && !canScrollNext) return null;

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn('absolute', orientation === 'horizontal' ? 'top-1/2 -right-12 -translate-y-1/2' : '-bottom-24 left-1/2 -translate-x-1/2 rotate-90', className)}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ChevronRight size="small" />

      <span className="sr-only">Next slide</span>
    </Button>
  );
}

export { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, useCarousel, type CarouselApi, type CarouselOptions };
